import React, { FC, useRef, ReactNode, useCallback, useLayoutEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  GetTables_Venue_floorPlans_tables as TableType,
  GetTables_Venue_floorPlans_markers as MarkerType,
  GetTables_Venue_floorPlans_structures as StructureType,
} from '~Scenes/Venue/__queries__';
import { TableComponentProps, TableKey } from '../../types';
import { useLogger } from '~Hooks';
import DragChequeHandler from './DragChequeHandler';
import Marker from '~Components/Marker';
import Structure from '~Components/Structure';
import { HANDY_SCALE_FACTOR } from './constants';

/**
 * Wrapper acts as the visible viewport for the floor plan. The ZoomWrapper inside
 * is transformed (translate + scale) to fit and zoom — so the wrapper itself never
 * needs to scroll.
 */
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 86px); /* 86px is the height of the top bar */
  overflow: hidden;
  box-sizing: border-box;
  touch-action: none; /* Disable default touch scrolling — we handle pan/zoom ourselves */
  transition: box-shadow 0.2s ease-in-out;
`;

/**
 * ZoomWrapper holds every floor plan item. transform-origin is set to the top-left so
 * the translate+scale math below stays predictable (a point p in local coords maps
 * to p * scale + pan in screen coords).
 */
const ZoomWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-origin: 0 0;
  will-change: transform;
`;

/** Re-export for backward compatibility */
export { HANDY_SCALE_FACTOR } from './constants';

export const InnerWrapper = styled.div<{
  posY: number;
  posX: number;
}>`
  position: absolute;
  top: ${(props) => props.posY * HANDY_SCALE_FACTOR}px;
  left: ${(props) => props.posX * HANDY_SCALE_FACTOR}px;
`;

interface Props {
  tables: TableType[];
  markers: MarkerType[];
  structures: StructureType[];
  tableComponent: ({ table, keyType, isMapView }: TableComponentProps) => ReactNode;
  chequeIsDragging: boolean;
}

/** Maximum pinch-zoom level (relative to the natural HANDY_SCALE_FACTOR coordinate system) */
const MAX_SCALE = 3;

/**
 * Horizontal padding (screen px) on each side of the fitted content. Larger value = more
 * zoom-out, leaving breathing room around the bounding box on initial render.
 */
const FIT_PADDING_X = 32;

/** Top padding (screen px) above the fitted content on initial render. */
const FIT_PADDING_TOP = 16;

/**
 * Bottom safe area (screen px) reserved for the floating action buttons rendered in
 * TableLayout (ButtonWrapper is position: fixed; bottom: 12px; height 40px). Without
 * this, the buttons cover the bottom-most table in the floor plan.
 */
const BOTTOM_BUTTONS_SAFE_AREA = 80;

/**
 * Approximate rendered footprint (including margin) of a handy table in unscaled coords.
 * Real table widths vary by shape (48–98px) plus 10px margin — this estimate is good enough
 * for bounding-box math and keeps the fit slightly generous rather than tight.
 */
const TABLE_ESTIMATED_SIZE = 68;

/**
 * Floor plan view used in Handy. All items (tables, markers, structures) are laid out using
 * their database posX/posY (scaled by HANDY_SCALE_FACTOR for the smaller Handy screen) and
 * the whole layer is then translated + scaled to:
 *   1. Fit the full bounding box inside the Handy viewport on initial render — so the user
 *      sees every table without having to pan or pinch out first.
 *   2. Allow the user to pinch-zoom in (and pan once zoomed in) to find the right table.
 */
const TableMap: FC<Props> = ({
  tables,
  markers,
  structures,
  tableComponent,
  chequeIsDragging,
}) => {
  const logger = useLogger('handy-tablemap');

  /** Current zoom level applied to ZoomWrapper */
  const scaleRef = useRef<number>(1);
  /** Current pan offset (translate) applied to ZoomWrapper */
  const panOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  /**
   * The "fit-to-screen" scale captured the first time we measure the wrapper. Used as
   * the lower bound for pinch-out — there's no value in zooming out past "everything visible".
   */
  const initialScaleRef = useRef<number>(1);

  const zoomWrapperRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastTouchPosRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchDistanceRef = useRef<number | null>(null);

  /** True if the current gesture had 2+ touches at any point */
  const didPinchRef = useRef<boolean>(false);

  /** Timestamp (ms) until which clicks are swallowed — set briefly after a pinch ends */
  const suppressClickUntilRef = useRef<number>(0);

  /**
   * Bounding box that contains every visible item (tables, markers, structures) in unscaled
   * floor-plan coordinates. We account for each item's approximate size so the fit covers
   * the full visible area — not just the top-left position of each item.
   *
   * Note: structures use posX/posY as their CENTER (per Structure component), while tables
   * and markers use posX/posY as their top-left.
   */
  const contentBounds = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [];

    tables.forEach((t) => {
      xs.push(t.posX, t.posX + TABLE_ESTIMATED_SIZE);
      ys.push(t.posY, t.posY + TABLE_ESTIMATED_SIZE);
    });

    markers.forEach((m) => {
      const size = m.size || 40;
      xs.push(m.posX, m.posX + size);
      ys.push(m.posY, m.posY + size);
    });

    structures.forEach((s) => {
      xs.push(s.posX - s.width / 2, s.posX + s.width / 2);
      ys.push(s.posY - s.height / 2, s.posY + s.height / 2);
    });

    /** Guard against an empty floor plan — Math.min/max of an empty list returns ±Infinity */
    if (xs.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }, [tables, markers, structures]);

  /** Bounding box dimensions in unscaled coords — passed to DragChequeHandler so it can apply the same fit */
  const mapWidth = contentBounds.maxX - contentBounds.minX + 2 * FIT_PADDING_X;
  const mapHeight = contentBounds.maxY - contentBounds.minY + 2 * FIT_PADDING_TOP;

  /**
   * Writes the current scale + pan to the ZoomWrapper via inline transform.
   * Bypasses React re-render on every touchmove frame for smooth interaction.
   */
  const updateZoomWrapper = () => {
    if (zoomWrapperRef.current) {
      zoomWrapperRef.current.style.transform = `translate(${panOffsetRef.current.x}px, ${panOffsetRef.current.y}px) scale(${scaleRef.current})`;
    }
  };

  /**
   * Callback ref that (re-)applies the current transform whenever ZoomWrapper mounts.
   * Critical for cheque drag mode: the conditional return below unmounts ZoomWrapper while
   * a cheque is being dragged, so when we come back the new DOM element starts with no
   * inline transform — without this, the user's zoom would silently snap back to scale 1.
   * Memoized with [] so React only invokes it when the underlying element changes.
   */
  const setZoomWrapperRef = useCallback((node: HTMLDivElement | null) => {
    zoomWrapperRef.current = node;
    if (node) {
      node.style.transform = `translate(${panOffsetRef.current.x}px, ${panOffsetRef.current.y}px) scale(${scaleRef.current})`;
    }
  }, []);

  /**
   * Computes the scale that fits the full bounding box horizontally inside the wrapper,
   * and the pan that places the content's left edge at FIT_PADDING. Content is centered
   * vertically when it's shorter than the wrapper.
   *
   * Re-runs whenever the floor plan items change (e.g. switching floor plan).
   */
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const contentWidthScaled = (contentBounds.maxX - contentBounds.minX) * HANDY_SCALE_FACTOR;
    const contentHeightScaled = (contentBounds.maxY - contentBounds.minY) * HANDY_SCALE_FACTOR;

    if (contentWidthScaled <= 0 || wrapperRect.width <= 0) {
      return;
    }

    /** Scale that makes the bounding box span exactly (wrapper width - 2 * padding) horizontally */
    const fitScale = (wrapperRect.width - 2 * FIT_PADDING_X) / contentWidthScaled;

    /**
     * With transform-origin 0 0, a point at local x maps to x*scale + panX on screen.
     * We want the left edge of content (minX * HANDY_SCALE_FACTOR) to appear at FIT_PADDING_X.
     */
    const fitPanX = FIT_PADDING_X - contentBounds.minX * HANDY_SCALE_FACTOR * fitScale;

    /**
     * Vertical placement: reserve a safe area at the bottom for the floating action buttons.
     * If the scaled content is shorter than the available area, center it inside that area;
     * otherwise top-align with FIT_PADDING_TOP so the user can pan downward to reveal the rest.
     */
    const scaledContentHeight = contentHeightScaled * fitScale;
    const availableHeight = wrapperRect.height - FIT_PADDING_TOP - BOTTOM_BUTTONS_SAFE_AREA;
    const fitPanY =
      scaledContentHeight < availableHeight
        ? FIT_PADDING_TOP +
          (availableHeight - scaledContentHeight) / 2 -
          contentBounds.minY * HANDY_SCALE_FACTOR * fitScale
        : FIT_PADDING_TOP - contentBounds.minY * HANDY_SCALE_FACTOR * fitScale;

    initialScaleRef.current = fitScale;
    scaleRef.current = fitScale;
    panOffsetRef.current = { x: fitPanX, y: fitPanY };

    updateZoomWrapper();

    logger.info('Fitted floor plan to wrapper width', {
      wrapperWidth: wrapperRect.width,
      contentWidthScaled,
      fitScale,
      tableCount: tables.length,
      markerCount: markers.length,
      structureCount: structures.length,
    });
    /**
     * Depend on the raw numeric bounds (not the contentBounds object) so this effect only
     * re-runs when the floor plan's geometry actually changes. If we depended on the object
     * reference, every parent re-render that passes a new tables/markers/structures array
     * would re-run the fit and snap the user back to the initial zoom — which is what made
     * tapping a table appear to "zoom out" on the user.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBounds.minX, contentBounds.maxX, contentBounds.minY, contentBounds.maxY]);

  /**
   * Constrains a pan offset so the content stays at least partially visible.
   * At the fit scale the content already fills the wrapper, so pan range is ~0;
   * once zoomed in, the user can pan around freely within the content bounds.
   */
  const constrainPanOffset = (offset: { x: number; y: number }, scale: number) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return offset;

    const wrapperRect = wrapper.getBoundingClientRect();
    const contentWidthAtScale =
      (contentBounds.maxX - contentBounds.minX) * HANDY_SCALE_FACTOR * scale;
    const contentHeightAtScale =
      (contentBounds.maxY - contentBounds.minY) * HANDY_SCALE_FACTOR * scale;
    const contentLeftAtScale = contentBounds.minX * HANDY_SCALE_FACTOR * scale;
    const contentTopAtScale = contentBounds.minY * HANDY_SCALE_FACTOR * scale;

    /** Keep at least EDGE_PADDING of content visible on every edge of the viewport */
    const EDGE_PADDING = 40;

    const minPanX = wrapperRect.width - EDGE_PADDING - contentLeftAtScale - contentWidthAtScale;
    const maxPanX = EDGE_PADDING - contentLeftAtScale;

    /**
     * Bottom pan limit accounts for the floating action buttons: subtract BOTTOM_BUTTONS_SAFE_AREA
     * so the user can pan content up far enough that the bottom-most table clears the buttons.
     */
    const minPanY =
      wrapperRect.height -
      EDGE_PADDING -
      BOTTOM_BUTTONS_SAFE_AREA -
      contentTopAtScale -
      contentHeightAtScale;
    const maxPanY = EDGE_PADDING - contentTopAtScale;

    /**
     * When the content is smaller than the wrapper (e.g. at fit scale), min/max can invert.
     * Use a stable clamp that handles either ordering.
     */
    const clamp = (value: number, a: number, b: number) =>
      Math.min(Math.max(value, Math.min(a, b)), Math.max(a, b));

    return {
      x: clamp(offset.x, minPanX, maxPanX),
      y: clamp(offset.y, minPanY, maxPanY),
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length >= 2) {
      /** Two fingers down — track pinch distance and mark gesture as pinch */
      didPinchRef.current = true;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      lastTouchDistanceRef.current = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );
    } else if (e.touches.length === 1) {
      /** Single finger — start tracking pan */
      const touch = e.touches[0];
      lastTouchPosRef.current = { x: touch.clientX, y: touch.clientY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 2) {
      /** Pinch zoom — adjust scale and re-anchor pan so the pinch midpoint stays put */
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );

      if (lastTouchDistanceRef.current !== null) {
        const scaleChange = distance / lastTouchDistanceRef.current;
        /** Lower bound is the fit scale — there's no value in zooming out past "everything visible" */
        const minScale = initialScaleRef.current;
        const newScale = Math.max(minScale, Math.min(MAX_SCALE, scaleRef.current * scaleChange));

        const wrapper = wrapperRef.current!;
        const wrapperRect = wrapper.getBoundingClientRect();
        const midX = (touch1.clientX + touch2.clientX) / 2 - wrapperRect.left;
        const midY = (touch1.clientY + touch2.clientY) / 2 - wrapperRect.top;

        /**
         * Re-anchor pan so the screen point (midX, midY) maps to the same local point
         * before and after the scale change. With transform-origin 0 0:
         *   localX = (midX - panX) / oldScale
         *   newPanX = midX - localX * newScale
         */
        const newPanX = midX - (midX - panOffsetRef.current.x) * (newScale / scaleRef.current);
        const newPanY = midY - (midY - panOffsetRef.current.y) * (newScale / scaleRef.current);

        scaleRef.current = newScale;
        panOffsetRef.current = constrainPanOffset({ x: newPanX, y: newPanY }, newScale);

        lastTouchDistanceRef.current = distance;
        updateZoomWrapper();
      }
    } else if (e.touches.length === 1) {
      /** Single-finger drag — pan by the delta since the last touch event */
      const touch = e.touches[0];
      if (lastTouchPosRef.current) {
        const deltaX = touch.clientX - lastTouchPosRef.current.x;
        const deltaY = touch.clientY - lastTouchPosRef.current.y;

        panOffsetRef.current = constrainPanOffset(
          {
            x: panOffsetRef.current.x + deltaX,
            y: panOffsetRef.current.y + deltaY,
          },
          scaleRef.current,
        );

        lastTouchPosRef.current = { x: touch.clientX, y: touch.clientY };
        updateZoomWrapper();
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    /** After a pinch, swallow the synthetic click so it doesn't select a table */
    if (didPinchRef.current && e.touches.length === 0) {
      suppressClickUntilRef.current = Date.now() + 400;
      didPinchRef.current = false;
      logger.info('Pinch ended — suppressing synthetic click for 400ms');
    }

    lastTouchDistanceRef.current = null;
    lastTouchPosRef.current = null;
  };

  /** Swallows the post-pinch synthetic click before it reaches the table */
  const handleClickCapture = (e: React.MouseEvent) => {
    if (Date.now() < suppressClickUntilRef.current) {
      e.stopPropagation();
      e.preventDefault();
      suppressClickUntilRef.current = 0;
      logger.info('Swallowed post-pinch synthetic click');
    }
  };

  if (chequeIsDragging) {
    return (
      <DragChequeHandler
        tables={tables}
        markers={markers}
        structures={structures}
        tableComponent={tableComponent}
        mapWidth={mapWidth}
        mapHeight={mapHeight}
      />
    );
  }

  return (
    <Wrapper
      id="tablemap-wrapper"
      ref={wrapperRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClickCapture={handleClickCapture}>
      <ZoomWrapper ref={setZoomWrapperRef}>
        {tables.map((table) => (
          <InnerWrapper key={table.id} posX={table.posX} posY={table.posY}>
            {tableComponent({
              table,
              keyType: TableKey['floor_plan'],
              isMapView: true,
            })}
          </InnerWrapper>
        ))}
        {markers.map((marker) => (
          <InnerWrapper key={marker.id} posX={marker.posX} posY={marker.posY}>
            <Marker icon={marker.icon} size={marker.size} isHandy />
          </InnerWrapper>
        ))}
        {structures.map((structure) => {
          return (
            <Structure
              key={structure.id}
              posX={structure.posX}
              posY={structure.posY}
              width={structure.width}
              height={structure.height}
              rotation={(structure as any).rotation || 0}
              isHandy
            />
          );
        })}
      </ZoomWrapper>
    </Wrapper>
  );
};

export default TableMap;
